import { Navigate, useLocation } from "react-router-dom";

/*** Backend + navigation use `{ allowed: true }`; some paths may still use bare booleans. */
const isPermissionAllowed = (permissions, key) => {
  const p = permissions?.[key];
  if (p === true) return true;
  if (p && typeof p === "object" && p.allowed === true) return true;
  return false;
};

// Helper function to find first accessible route for a user
// Matches the order of navigation items and only returns routes user has permission for
const getFirstAccessibleRoute = (userRole, permissions) => {
  console.log('🔍 getFirstAccessibleRoute called:', { userRole, permissions });
  
  // Admin always has access to analytics
  if (userRole === 'Admin') {
    console.log('✅ Admin user - returning /dashboard/analytics');
    return '/dashboard/analytics';
  }
  
  // Company users default to products-management
  if (userRole === 'Company') {
    console.log('✅ Company user - returning /dashboard/products-management');
    return '/dashboard/products-management';
  }
  
  // Same order as navigation.js; use .allowed, not truthy objects
  if (isPermissionAllowed(permissions, 'dashboard') || isPermissionAllowed(permissions, 'analytics')) {
    return '/dashboard/analytics';
  }
  if (isPermissionAllowed(permissions, 'appOrders')) {
    return '/dashboard/orders-management';
  }
  if (isPermissionAllowed(permissions, 'talkToAgronomist')) {
    return '/dashboard/agronomist';
  }
  if (isPermissionAllowed(permissions, 'bulkOrders')) {
    return '/dashboard/bulk-upload';
  }
  if (isPermissionAllowed(permissions, 'returnCancelRequest')) {
    return '/dashboard/return-cancel-request';
  }
  if (isPermissionAllowed(permissions, 'quotation')) {
    return '/dashboard/Quotation-proforma';
  }
  if (isPermissionAllowed(permissions, 'farmerManagement')) {
    return '/dashboard/farmers';
  }
  if (isPermissionAllowed(permissions, 'agriStore')) {
    return '/dashboard/products-management';
  }
  if (isPermissionAllowed(permissions, 'groceries')) {
    return '/dashboard/groceries';
  }
  if (isPermissionAllowed(permissions, 'farmerForum')) {
    return '/dashboard/posts';
  }
  if (isPermissionAllowed(permissions, 'successStories')) {
    return '/dashboard/courses';
  }
  if (isPermissionAllowed(permissions, 'eventType')) {
    return '/dashboard/event-types';
  }
  if (isPermissionAllowed(permissions, 'cropType')) {
    return '/dashboard/crop-types';
  }
  if (isPermissionAllowed(permissions, 'enquiry')) {
    return '/dashboard/enquiry-form';
  }
  if (isPermissionAllowed(permissions, 'fieldVisit')) {
    return '/dashboard/field-visit';
  }
  if (isPermissionAllowed(permissions, 'category')) {
    return '/dashboard/invoice';
  }
  if (isPermissionAllowed(permissions, 'cropHub')) {
    return '/dashboard/crop-hub';
  }
  if (isPermissionAllowed(permissions, 'diseaseAdvisory')) {
    return '/dashboard/disease-advisory';
  }
  if (isPermissionAllowed(permissions, 'aiChatBot')) {
    return '/dashboard/ai-chat-bot';
  }
  if (isPermissionAllowed(permissions, 'users')) {
    return '/dashboard/users';
  }
  if (isPermissionAllowed(permissions, 'permissions')) {
    return '/dashboard/permissions';
  }
  if (
    isPermissionAllowed(permissions, 'FAQs') ||
    isPermissionAllowed(permissions, 'faq') ||
    isPermissionAllowed(permissions, 'FAQ')
  ) {
    return '/dashboard/FAQ';
  }

  console.log('⚠️ No permissions found at all - returning /dashboard/products-management as default');
  return '/dashboard/products-management';
};

const ProtectedRoute = ({ children, allowedRoles = [], requiredPermission = null }) => {
  const location = useLocation();
  const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
  const userData = loggedData?.data;
  const isAuthenticated = !!userData;
  const userRole = userData?.user_type;
  const permissions = userData?.permissions || {};
  console.log('------------permissions--------------------');
  console.log(requiredPermission);
  console.log('------------userRole--------------------');
  console.log(userRole);
  console.log('------------isAuthenticated--------------------');
  console.log(isAuthenticated);
  console.log('------------allowedRoles--------------------');
  console.log(allowedRoles);
  console.log('------------requiredPermission--------------------');
  console.log('🚀 ProtectedRoute - Entry:', {
    pathname: location.pathname,
    userRole,
    isAuthenticated,
    allowedRoles,
    requiredPermission,
    permissions
  });
  
  // If user is not authenticated, redirect to login
  if (!isAuthenticated) {
    console.log('❌ User not authenticated - redirecting to /login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Block Farmer role from accessing dashboard - clear localStorage and redirect
  if (userRole === 'Farmer') {
    console.log('❌ Farmer role detected - clearing localStorage and redirecting to /login');
    localStorage.clear();
    return <Navigate to="/login" replace />;
  }
  
  // If route requires specific roles and user's role is not included
  // Only check roles if allowedRoles is provided and not empty
  if (allowedRoles.length > 0 && !allowedRoles.includes(userRole)) {
    // User's role is not in the allowed list - redirect to first accessible route
    // This handles routes like company-category-management which only allow Admin and Company
    console.log(`❌ User role '${userRole}' not in allowedRoles [${allowedRoles.join(', ')}] - redirecting to first accessible route`);
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
    return <Navigate to={firstAccessibleRoute} replace />;
  }
  
  // If permission is required, check it (for all users including Admin/Company)
  if (requiredPermission) {
    console.log(`🔐 Checking required permission: '${requiredPermission}'`);
    
    // Special handling for dashboard/analytics
    if (requiredPermission === 'dashboard' || requiredPermission === 'analytics') {
      console.log('📊 Analytics/Dashboard permission check');
      // Admin and Company always have access to dashboard/analytics
      if (userRole === 'Admin' || userRole === 'Company') {
        console.log(`✅ ${userRole} has access to analytics - allowing`);
        // Allow access for Admin and Company
      } else if (
        !isPermissionAllowed(permissions, 'dashboard') &&
        !isPermissionAllowed(permissions, 'analytics')
      ) {
        // Other roles need explicit permission for analytics
        console.log(`❌ ${userRole} doesn't have analytics/dashboard permission`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      } else {
        console.log(`✅ ${userRole} has analytics/dashboard permission - allowing`);
      }
    } else if (userRole === 'Admin') {
      // Admin has access to everything
      console.log('✅ Admin has access to everything - allowing');
      // No check needed, allow access
    } else if (userRole === 'Company') {
      console.log('🏢 Company role - checking company allowed permissions');
      // Company role has access to common routes even without explicit permissions
      // Map route permissions to allowed permissions for Company
      const companyAllowedPermissions = [
        'agriStore',        // products-management
        'appOrders',        // orders-management
        'bulkOrders',       // bulk-upload
        'quotation',        // Quotation-proforma
        'enquiry',          // enquiry-form
        'farmerManagement', // farmers
        'talkToAgronomist', // agronomist
        'fieldVisit',       // field-visit
        'diseaseAdvisory',  // disease-advisory
        'eventType',        // event-types
        'cropType',         // crop-types
        'farmerForum',      // posts
        'successStories',   // courses
        'cropHub'           // crop-hub
      ];
      // Company users can access these routes even without explicit permission
      // But if permission is required and not in allowed list, check if user has it
      if (
        !companyAllowedPermissions.includes(requiredPermission) &&
        !isPermissionAllowed(permissions, requiredPermission)
      ) {
        // Permission not in Company's allowed list and user doesn't have it
        console.log(`❌ Company doesn't have permission '${requiredPermission}' and it's not in allowed list`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      }
      // If permission is in allowed list, allow access regardless of permissions object
      console.log(`✅ Company has access to '${requiredPermission}' - allowing`);
    } else {
      // Other roles (co_admin, developer, accounts, operations, etc.) need explicit permission
      console.log(`👤 Other role '${userRole}' - checking explicit permission`);
      // Check if user has the required permission
      // permission may be stored under multiple keys for FAQ
      let hasPermission;
      if (requiredPermission === 'FAQs') {
        hasPermission =
          isPermissionAllowed(permissions, 'FAQs') ||
          isPermissionAllowed(permissions, 'faq') ||
          isPermissionAllowed(permissions, 'FAQ');
      } else {
        hasPermission = isPermissionAllowed(permissions, requiredPermission);
      }
      console.log(`Permission '${requiredPermission}': ${hasPermission}`, { 
        permissionValue: permissions[requiredPermission],
        allPermissions: permissions 
      });
      
      if (!hasPermission) {
        // User doesn't have the required permission
        // Redirect to their first accessible route based on their actual permissions
        console.log(`❌ User '${userRole}' doesn't have permission '${requiredPermission}'`);
        const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
        console.log(`➡️ Redirecting to: ${firstAccessibleRoute}`);
        return <Navigate to={firstAccessibleRoute} replace />;
      }
      // User has the required permission, allow access
      console.log(`✅ User '${userRole}' has permission '${requiredPermission}' - allowing`);
    }
  } else {
    console.log('ℹ️ No required permission - allowing access');
  }

  // Redirect from dashboard root to appropriate page based on permissions
  if (location.pathname === '/dashboard') {
    console.log('🏠 Dashboard root path - redirecting to first accessible route');
    // Use the helper function to get the first accessible route
    // This ensures consistency and matches navigation order
    const firstAccessibleRoute = getFirstAccessibleRoute(userRole, permissions);
    console.log(`➡️ Redirecting from /dashboard to: ${firstAccessibleRoute}`);
    return <Navigate to={firstAccessibleRoute} replace />;
  }

  console.log('✅ All checks passed - allowing access to:', location.pathname);
  return children;
};

export default ProtectedRoute; 